import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
// src/pages/a6/portfolios/edit_card.tsx

import { z } from "zod";

import {
  adjustDataForSubmission,
  countries_options,
  preprocessCountryData,
} from "@/components/Form/CountryPhone/data";
import { Form } from "@/components/ui/form";
import i18n from "@/i18n";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
} from "@heroui/react";

import { Diff } from "@/features/FormCard/Diff";

import {
  AutocompleteField,
  AutocompleteLabelField,
  CheckboxField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";

import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { parseYes } from "@/features/FormCard/utils";

import { FormActionsUpdate } from "@/features/FormCard/Actions";

import { ExternalLink } from "@/components/ExternalLink";
import { useEditForm } from "@/features/Edit/hooks";
import useCurrencyOptions from "@/hooks/useCurrencyOption";
import { Label } from "@radix-ui/react-label";
import { fieldTranslations } from "./translations";
import { PortfoliosTechnicalInfoSection } from "@/components/PortfoliosTechnicalInfoSection";

const FormSchema = z.object({
  has_transactions: z.boolean().optional().nullable(),
  client: z
    .string()
    .nullable()
    .optional()
    .transform((val) => (val ? val.toUpperCase() : val)),
  vehicle: z.string().nullable().optional(),

  account_name: z.string().nullable().optional(),
  short_account_name: z.string().nullable().optional(),
  name: z.string().nullable().optional(),

  currency: z.string().nullable().optional(),
  reporting_currency: z.string().nullable().optional(),

  bank_name: z.string().nullable().optional(),
  account_type: z.string().nullable().optional(),

  investment_type: z.string().nullable().optional(),
  contract_name: z.string().nullable().optional(),

  insurance_company: z.string().nullable().optional(),
  contract_owner: z.string().nullable().optional(),

  code: z.string().nullable().optional(),
  strategy_id: z.coerce.number().nullable().optional(),

  has_pdf_bank_page: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional().nullable(),
  ),

  display_performance_report: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional().nullable(),
  ),

  bank_connection: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional().nullable(),
  ),
  bank_reporting_currency: z.string().nullable().optional(),

  collateral: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional(),
  ),
  vehicle_index: z.coerce.number().int().nullable().optional(),
  bank_country: z.string().nullable().optional(),

  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});

export function A6PortfoliosEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    isDeleting,
    changedFields,
    options,
    handleDelete,
    fetchedData,
  } = useEditForm({
    id,
    client,
    table_name,
    FormSchema,
    adjustData: adjustDataForSubmission,
    adjustOnReset: preprocessCountryData,
  });

  const currencyOptions = useCurrencyOptions();
  const { t } = useTranslation();
  const name = form.watch("name");

  return (
    <>
      <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
        <div className="flex justify-center w-full">
          <Card className="p-4 bg-white w-full ">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <CardHeader className="text-lg font-bold text-gray-700">
                  {i18n.t("portfolios.portfolio")} {id}
                </CardHeader>
                <Divider />

                <CardBody className="flex flex-col gap-4">
                  <Label>{i18n.t("sections.client_info")}</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField
                      name="client"
                      label={i18n.t("a6_portfolios.columns.client", {
                        ns: "tables",
                        defaultValue: "Client",
                      })}
                      isRequired
                      control={form.control}
                    />
                    <InputField
                      name="vehicle"
                      label={i18n.t("a6_portfolios.columns.vehicle", {
                        ns: "tables",
                        defaultValue: "Véhicule / Bénéficiaire du compte",
                      })}
                      control={form.control}
                      isRequired
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <AutocompleteLabelField
                      name="reporting_currency"
                      label={i18n.t(
                        "a6_portfolios.columns.reporting_currency",
                        {
                          ns: "tables",
                          defaultValue: "Devise de reporting",
                        },
                      )}
                      options={currencyOptions}
                      isRequired
                      control={form.control}
                      className="min-w-0"
                    />
                  </div>

                  <Divider />
                  <Label>{i18n.t("sections.account_info")}</Label>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField
                      name="name"
                      label={i18n.t("a6_portfolios.columns.name", {
                        ns: "tables",
                        defaultValue: "Compte",
                      })}
                      isRequired
                      control={form.control}
                    />
                    <InputField
                      name="account_name"
                      label={i18n.t("a6_portfolios.columns.account_name", {
                        ns: "tables",
                        defaultValue: "Groupe de comptes",
                      })}
                      isRequired
                      control={form.control}
                    />
                  </div>

                  {/* New: short account name below Compte & Groupe */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField
                      name="short_account_name"
                      label={i18n.t(
                        "a6_portfolios.columns.short_account_name",
                        {
                          ns: "tables",
                          defaultValue: "Nom court du compte",
                        },
                      )}
                      control={form.control}
                    />
                    <AutocompleteField
                      name="account_type"
                      label={i18n.t("a6_portfolios.columns.account_type", {
                        ns: "tables",
                        defaultValue: "Type de compte",
                      })}
                      options={options?.["account_type"] ?? []}
                      isRequired
                      control={form.control}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <AutocompleteLabelField
                      name="currency"
                      label={i18n.t("a6_portfolios.columns.currency", {
                        ns: "tables",
                        defaultValue: "Devise du portefeuille",
                      })}
                      options={currencyOptions}
                      isRequired
                      control={form.control}
                      className="min-w-0"
                    />
                    <InputField
                      name="code"
                      label={i18n.t("a6_portfolios.columns.code", {
                        ns: "tables",
                        defaultValue: "Code",
                      })}
                      isRequired
                      control={form.control}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField
                      name="bank_name"
                      label={i18n.t("a6_portfolios.columns.bank_name", {
                        ns: "tables",
                        defaultValue: "Banque",
                      })}
                      isRequired
                      control={form.control}
                    />
                    <SelectField
                      name="bank_country"
                      label={t(`${table_name}.columns.bank_country`, {
                        ns: "tables",
                        defaultValue: "Country",
                      })}
                      options={countries_options}
                      control={form.control}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField
                      name="investment_type"
                      label={i18n.t("a6_portfolios.columns.investment_type", {
                        ns: "tables",
                        defaultValue: "Type d'investissement",
                      })}
                      control={form.control}
                    />
                    <InputField
                      name="contract_name"
                      label={i18n.t("a6_portfolios.columns.contract_name", {
                        ns: "tables",
                        defaultValue: "Nom du contrat",
                      })}
                      control={form.control}
                    />
                  </div>

                  {/* A6 only: below investment_type & contract_name */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField
                      name="insurance_company"
                      label={i18n.t("a6_portfolios.columns.insurance_company", {
                        ns: "tables",
                        defaultValue: "Compagnie d’assurance",
                      })}
                      control={form.control}
                    />
                    <InputField
                      name="contract_owner"
                      label={i18n.t("a6_portfolios.columns.contract_owner", {
                        ns: "tables",
                        defaultValue: "Titulaire du contrat d’assurance",
                      })}
                      control={form.control}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <AutocompleteLabelField
                      name="bank_reporting_currency"
                      label={t(
                        `${table_name}.columns.bank_reporting_currency`,
                        {
                          ns: "tables",
                        },
                      )}
                      options={currencyOptions}
                      isRequired={false}
                      control={form.control}
                      className="min-w-0"
                    />
                    <CheckboxField
                      name="collateral"
                      label={i18n.t("a6_portfolios.columns.collateral", {
                        ns: "tables",
                        defaultValue: "Compte-titres nanti",
                      })}
                      control={form.control}
                      className="flex h-full items-center"
                    />
                  </div>

                  <PortfoliosTechnicalInfoSection
                    id={id}
                    form={form}
                    table_name={table_name}
                  />
                </CardBody>

                <CardFooter>
                  <FormActionsUpdate
                    onReset={handleReset}
                    onDelete={openDeleteModal}
                    isDirty={form.formState.isDirty}
                    elementId={id}
                    client={client}
                    allowDelete={false}
                  />
                </CardFooter>
              </form>
            </Form>
          </Card>

          {name && fetchedData?.has_transactions && (
            <div className="absolute top-12 right-12 2xl:top-10 2xl:right-10 md:flex gap-2 flex-row hidden">
              <Button
                href={toWorkspacePath("/admin/a6/a6_transactions")}
                variant="bordered"
                color="primary"
                showAnchorIcon
                as={ExternalLink}
                radius="full"
                queryParams={{ a6_portfolios_id_equal: id }}
                className="bg-white border-1 border-neutral-300 shadow-xl"
              >
                {i18n.t("actions.transactions")}
              </Button>
            </div>
          )}

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={i18n.t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, [], fieldTranslations, table_name)
              : i18n.t("no_changes")}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </div>
      </div>
    </>
  );
}
